import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';

import Localizable from 'lib/intl/localizable';
import Styled from 'styled';

import CommentView, { CommentViewModel } from 'four-nets/common/comment/comment-view';
import CommentHeaderView from 'four-nets/bazaar/review/review-comment-header-view';
import CommentFooterView from 'four-nets/bazaar/review/review-comment-footer-view';
import CreateEditReplyView from 'four-nets/bazaar/review/create-edit-reply-view';
import { Review } from 'four-nets/bazaar/bazaar';
import { CreateEditReplyForm } from 'four-nets/bazaar/review/create-edit-reply-view';

import defaultStyles from './review-view.scss';

export interface Props {
    styles: typeof defaultStyles;
    review: Review;
    fromSellers: boolean;
    forceUpdate: () => void;
}

@Localizable()
@observer
class ReviewView extends React.Component<Props> {
    ref?: any;
    form: any;

    constructor(props: Props) {
        super(props);

        this.form = new CreateEditReplyForm({
            handleSubmit: this.handleSubmit,
            message: props.review.reply,
        });
    }

    setRef = (ref: React.ReactNode) => {
        this.ref = ref;
    };

    handleSubmit = ({ data, form }: { data: { message: string }; form: CreateEditReplyForm }) => {
        const { message } = data;
        return this.props.review.sendReply(message, this.props.fromSellers);
    };

    get reviewerComment() {
        const { review } = this.props;
        return {
            user: review.reviewer,
            time: review.reviewedDate,
            body: review.message,
            displayedContent: review.message,
        };
    }

    get replyComment() {
        const { review } = this.props;
        return {
            user: review.user,
            time: review.repliedDate,
            body: review.reply,
            displayedContent: review.reply,
        };
    }

    get commentViewModel() {
        return new CommentViewModel({
            comment: this.reviewerComment,
        });
    }

    get replyViewModel() {
        return new CommentViewModel({
            comment: this.replyComment,
        });
    }

    deleteReply = () => {
        return this.props.review.deleteReply(this.props.fromSellers);
    };

    delete = () => {
        return this.props.review.delete(this.props.fromSellers);
    };

    open = () => {
        if (this.ref) {
            this.ref.show({ form: this.form });
        }
    };

    render() {
        const { review } = this.props;

        const styles = this.props.styles!;

        return (
            <div className={styles.wrapper}>
                <CreateEditReplyView innerRef={this.setRef} form={this.form} />
                <CommentView
                    commentViewModel={this.commentViewModel}
                    customHeader={
                        <CommentHeaderView
                            review={review}
                            commentHeaderViewModel={this.commentViewModel.commentBodyViewModel.commentHeaderViewModel}
                        />
                    }
                    customFooter={
                        review.canDelete || review.canReply ? (
                            <CommentFooterView
                                reply={review.canReply ? this.open : undefined}
                                delete={review.canDelete ? this.delete : undefined}
                                ensureMessage={
                                    <span>
                                        <FormattedMessage
                                            id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW'
                                            values={{ gender: true }}
                                        />
                                        ?
                                    </span>
                                }
                                okMessage={<FormattedMessage id='YOUR_REVIEW_WAS_REMOVED' />}
                                reload={this.props.forceUpdate}
                            />
                        ) : null
                    }
                />
                {review.reply ? (
                    <div className={styles.reply}>
                        <CommentView
                            commentViewModel={this.replyViewModel}
                            customFooter={
                                review.canDeleteReply ? (
                                    <CommentFooterView
                                        delete={review.canDeleteReply ? this.deleteReply : undefined}
                                        ensureMessage={
                                            <span>
                                                <FormattedMessage
                                                    id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW_REPLY'
                                                    values={{ gender: true }}
                                                />
                                                ?
                                            </span>
                                        }
                                        okMessage={<FormattedMessage id='YOUR_REVIEW_WAS_REMOVED' />}
                                    />
                                ) : null
                            }
                        />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default Styled(defaultStyles)(ReviewView);
